<template>
  <div>
    <table-pagination class="mb-1" :pagination="pagination" :counter-label="$t('backup.tabs.notes')"
                      @page-change="onPaginationPage" @size-change="onPaginationSize"/>

    <b-table responsive class="bk-table" :items="rows" :fields="fields"
             no-local-sorting :sort-by="sort.field" :sort-desc="sortIsDesc"
             :busy="loading" hover show-empty :tbody-tr-class="rowClass"
             @sort-changed="onSortChanged" @row-clicked="onRowClick">
      <template #table-busy>
        <div class="text-center text-primary my-2"><b-spinner small class="mr-50"/>{{ $t('backup.loading') }}</div>
      </template>
      <template #empty>
        <backup-table-state :error="loadError ? errorText() : null" icon="fa-regular fa-note-sticky"
                            :title="$t('backup.emptyNotes')" :hint="$t('backup.emptyHint')" @retry="refresh"/>
      </template>

      <template #head(select)>
        <b-form-checkbox :checked="allPagePicked" :indeterminate="somePagePicked"
                         :disabled="!selectableRows.length" @change="togglePagePick"/>
      </template>
      <template #cell(select)="{ item }">
        <span v-b-tooltip.hover.right="selectBlockReason(item)">
          <b-form-checkbox class="no-click" :checked="isPicked(item)" :disabled="!isSelectable(item)"
                           @change="v => togglePick(item, v)"/>
        </span>
      </template>

      <template #cell(time)="{ value, item }">
        <div class="bk-two">
          <span>{{ bkDateTime(value) }}</span>
          <small class="bk-id">#{{ item.backupId }}</small>
        </div>
      </template>
      <template #cell(clientBackupId)="{ item }">
        <backup-client-ref :name="item.clientName" :client-backup-id="item.clientBackupId"
                           :current-client-id="item.currentClientId"
                           :client-known="'currentClientId' in item"
                           @open-workspace="id => $emit('open-client', id)"/>
      </template>
      <template #cell(userId)="{ value }">
        <span v-if="value">{{ bkUserName(value) }}</span><span v-else class="text-muted">—</span>
      </template>
      <template #cell(text)="{ item }">
        <div class="bk-note">
          <i v-if="item.isPinned" class="fa-solid fa-thumbtack text-primary mr-25" v-b-tooltip.hover.top="$t('backup.pinned')"></i>
          <i v-if="item.isImportant" class="fa-solid fa-star text-warning mr-25" v-b-tooltip.hover.top="$t('backup.important')"></i>
          <span class="bk-note__text" :title="item.text">{{ item.text }}</span>
        </div>
      </template>
      <template #cell(matchStatus)="{ item }">
        <backup-status-badge :status="item.matchStatus" :reason="item.matchReason"
                             :current-id="item.currentId" :client-id="item.currentClientId"/>
      </template>
    </b-table>

    <table-pagination v-if="rows.length > 10" class="mt-1" :pagination="pagination"
                      :counter-label="$t('backup.tabs.notes')"
                      @page-change="onPaginationPage" @size-change="onPaginationSize"/>

    <backup-bulk-bar :count="pickedCount" :label="$tc('backup.bulk.notesSelected', pickedCount, { n: pickedCount })"
                     @clear="clearPicked" @restore="emitPlan"/>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import mixinBackupTable from '@/components/mixin/mixinBackupTable'
import { isRestorable, groupRestoreRequests } from '@/components/mixin/mixinBackup'
import TablePagination from '@/components/widget/TablePagination.vue'
import BackupStatusBadge from '@/components/partial/backup/BackupStatusBadge.vue'
import BackupTableState from '@/components/partial/backup/BackupTableState.vue'
import BackupBulkBar from '@/components/partial/backup/BackupBulkBar.vue'
import BackupClientRef from '@/components/partial/backup/BackupClientRef.vue'

export default {
  name: 'BackupNotesTable',
  components: { TablePagination, BackupStatusBadge, BackupTableState, BackupBulkBar, BackupClientRef },
  mixins: [mixinBackupTable],
  data: () => ({
    defaultSortField: 'time',
  }),
  computed: {
    fields() {
      return [
        { key: 'select', label: '', class: 'bk-col-select' },
        { key: 'time', label: this.$t('backup.columns.time'), sortable: true },
        { key: 'clientBackupId', label: this.$t('backup.columns.client'), sortable: true, class: 'bk-col-left' },
        { key: 'userId', label: this.$t('backup.columns.user') },
        { key: 'text', label: this.$t('backup.columns.note'), class: 'bk-col-note' },
        { key: 'matchStatus', label: this.$t('backup.columns.match'), sortable: true },
      ]
    },
  },
  methods: {
    ...mapActions('backup', ['getAllBackupNotes']),
    fetchRows(params) {
      return this.getAllBackupNotes(params)
    },
    // Rows that say their client is missing now are blocked; rows that don't
    // carry currentClientId are left to the server (it SKIPs/FAILs them).
    isSelectable(row) {
      if (!isRestorable(row.matchStatus)) return false
      return !('currentClientId' in row) || (row.currentClientId != null && row.currentClientId !== '')
    },
    selectBlockReason(row) {
      if (this.isSelectable(row)) return ''
      if (!isRestorable(row.matchStatus)) return this.$t('backup.notRestorable')
      return this.$t('backup.restoreClientFirst')
    },
    onRowClick(row, index, ev) {
      if (ev && ev.target && ev.target.closest && ev.target.closest('.no-click, .custom-control, a, button')) return
      if (this.isSelectable(row)) this.togglePick(row, !this.isPicked(row))
    },
    rowClass(item) {
      if (!item) return ''
      // status bar: orange = something to recover/review, green = 100% the same
      const same = item.matchStatus === 'EXISTS' || item.matchStatus === 'RESTORED'
      return ['bk-row', `bk-row--${(item.matchStatus || '').toLowerCase()}`, same ? 'bk-row--same' : 'bk-row--todo',
        this.isPicked(item) ? 'bk-row--picked' : '', this.isSelectable(item) ? 'bk-row--selectable' : '']
    },
    emitPlan() {
      const notes = this.pickedRows
      this.$emit('restore-plan', {
        source: 'notes',
        sections: { client: null, accounts: [], fields: [], monetary: [], trades: [], notes },
        requests: groupRestoreRequests({ notes }),
      })
    },
    afterRestore() {
      this.clearPicked()
      this.refresh()
    },
  },
}
</script>
